/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRevalidateFilledFields } from '@/hooks/use-revalidate-filled-fields';
import { toast } from 'sonner';
import type { ShopperCustomers } from '@/scapi';
import { ToggleCard, ToggleCardEdit, ToggleCardSummary } from '@/components/toggle-card';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FloatingLabelInput } from '@/components/ui/floating-label-input';
import { useBasket } from '@/providers/basket';
import { useAuth } from '@/providers/auth';
import {
    createShippingAddressSchema,
    NAME_PATTERN,
    INDIAN_MOBILE_PATTERN,
    validMessage,
    type ShippingAddressData,
} from '@/lib/checkout/schemas';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import { useScapiFetcher } from '@/hooks/use-scapi-fetcher';
import { useScapiFetcherEffect } from '@/hooks/use-scapi-fetcher-effect';
import {
    getShippingAddressFromCustomer,
    getAddressBookFromCustomer,
    type AddressBookItem,
} from '@/lib/customer/profile-utils';
import { AddressFormFields } from '@/components/address-form-fields';
import SavedAddressDropdown from './saved-address-dropdown';
import type { CheckoutActionData } from '../types';
import { addressToFormData, getAddressKey, isAddressEmpty } from '@/lib/address/address-utils';
import { cn } from '@/lib/utils';
import ShippingAddressDisplay from './shipping-address-display';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { DEFAULT_COUNTRY_CODE } from '@/components/customer-address-form/constants';
import { stripCountryCode, extractCountryCode, stripNonDigits } from '@/lib/address/phone-utils';

interface ShippingAddressProps {
    onSubmit: (formData: FormData) => void;
    isLoading: boolean;
    actionData?: CheckoutActionData;
    // Step state managed by container
    isCompleted: boolean;
    isEditing: boolean;
    onEdit: () => void;
}

export default function ShippingAddress({
    onSubmit,
    isLoading,
    actionData,
    isCompleted,
    isEditing,
    onEdit,
}: ShippingAddressProps) {
    const cart = useBasket();
    const customerProfile = useCustomerProfile();
    const auth = useAuth();
    const customerId = auth?.customerId;
    const { t } = useTranslation('checkout');
    const { t: tErrors } = useTranslation('errors');

    // Signed-in shoppers get the two-stage flow: address form -> saved-address picker.
    const isSignedIn = Boolean(customerProfile?.customer);

    const shippingAddress = cart?.shipments?.[0]?.shippingAddress;

    // Show the shopper the address they typed so they can modify it
    const attemptedAddress = (actionData?.data as { address?: Partial<ShippingAddressData> } | undefined)?.address;
    const basketAddressDiffersFromAttempted = Boolean(
        actionData?.success &&
            attemptedAddress &&
            shippingAddress &&
            (attemptedAddress.address1 !== shippingAddress.address1 ||
                attemptedAddress.postalCode !== shippingAddress.postalCode ||
                attemptedAddress.stateCode !== shippingAddress.stateCode ||
                attemptedAddress.city !== shippingAddress.city)
    );
    const formSourceAddress = basketAddressDiffersFromAttempted ? attemptedAddress : undefined;

    // Get auto-populated shipping address from customer profile
    const customerShippingAddress = getShippingAddressFromCustomer(customerProfile);

    // Get phone from contact info (prioritize this for auto-population)
    const contactInfoPhone = cart?.customerInfo?.phone;

    // Phone priority: attempted (from last no-methods submit) > saved shipping > contact > profile
    const prioritizedPhoneNumber = (formSourceAddress?.phone ||
        shippingAddress?.phone ||
        contactInfoPhone ||
        customerShippingAddress.phone ||
        '') as string;
    const schema = useMemo(() => createShippingAddressSchema(t as unknown as TFunction), [t]);
    // Signed-in shoppers fill the address in fresh every visit — only name / phone carry over.
    const blankAddressFields = isSignedIn
        ? { address1: '', address2: '', city: '', stateCode: '', postalCode: '' }
        : undefined;
    const form = useForm<ShippingAddressData>({
        resolver: zodResolver(schema),
        // Show a field's error once it's been left, then re-check on every keystroke so it clears as soon as it's fixed
        mode: 'onTouched',
        defaultValues: {
            firstName:
                formSourceAddress?.firstName ||
                shippingAddress?.firstName ||
                customerShippingAddress.firstName ||
                (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('checkoutFirstName')) ||
                '',
            lastName:
                formSourceAddress?.lastName ||
                shippingAddress?.lastName ||
                customerShippingAddress.lastName ||
                (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('checkoutLastName')) ||
                '',
            address1:
                formSourceAddress?.address1 || shippingAddress?.address1 || customerShippingAddress.address1 || '',
            address2:
                formSourceAddress?.address2 || shippingAddress?.address2 || customerShippingAddress.address2 || '',
            city: formSourceAddress?.city || shippingAddress?.city || customerShippingAddress.city || '',
            stateCode:
                formSourceAddress?.stateCode || shippingAddress?.stateCode || customerShippingAddress.stateCode || '',
            postalCode:
                formSourceAddress?.postalCode ||
                shippingAddress?.postalCode ||
                customerShippingAddress.postalCode ||
                (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('checkoutPincode')) ||
                '',
            // India-only storefront: always start on India, even if an older saved address says otherwise
            countryCode: DEFAULT_COUNTRY_CODE,
            phoneCountryCode: extractCountryCode(prioritizedPhoneNumber),
            phone: stripCountryCode(prioritizedPhoneNumber),
            addressType:
                formSourceAddress?.addressType ||
                (shippingAddress as { c_addressType?: string } | undefined)?.c_addressType ||
                'office',
            ...blankAddressFields,
        },
    });

    // Autofilled fields show their errors immediately, and all invalid filled fields show together
    useRevalidateFilledFields(form);

    const hasBasketAddress = !isAddressEmpty(shippingAddress);

    // The form's defaultValues are captured on mount; if the basket address was set afterwards,
    // refill the form from it when the shopper hits Edit.
    useEffect(() => {
        if (isSignedIn || !isEditing || !shippingAddress || !hasBasketAddress) return;
        const phone = shippingAddress.phone || form.getValues('phone') || '';
        form.reset({
            ...form.getValues(),
            firstName: shippingAddress.firstName || '',
            lastName: shippingAddress.lastName || '',
            address1: shippingAddress.address1 || '',
            address2: shippingAddress.address2 || '',
            city: shippingAddress.city || '',
            stateCode: shippingAddress.stateCode || '',
            postalCode: shippingAddress.postalCode || '',
            countryCode: DEFAULT_COUNTRY_CODE,
            phoneCountryCode: extractCountryCode(phone),
            phone: stripCountryCode(phone),
            addressType:
                (shippingAddress as { c_addressType?: string }).c_addressType ||
                form.getValues('addressType') ||
                'office',
        });
        // Only when entering edit mode — not on every basket revalidation while typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing]);

    // --- Signed-in flow: "Save Address" stores the address in the address book, then the
    // picker (name / phone / saved-address dropdown) is shown; "Use this address" applies it.
    // Signed-in shoppers start on the picker; the full form only opens via "Add a New Address".
    const [signedInStage, setSignedInStage] = useState<'form' | 'picker'>('picker');
    // Addresses saved during this visit — shown in the dropdown even before the profile revalidates.
    const [justSavedAddresses, setJustSavedAddresses] = useState<AddressBookItem[]>([]);
    const [pickerSelectedId, setPickerSelectedId] = useState('');
    const [pickerFirstName, setPickerFirstName] = useState(() => form.getValues('firstName') || '');
    const [pickerLastName, setPickerLastName] = useState(() => form.getValues('lastName') || '');
    const [pickerPhone, setPickerPhone] = useState(() => stripNonDigits(form.getValues('phone') || '').slice(-10));
    const [pickerError, setPickerError] = useState<string | null>(null);
    // Picker fields whose errors show inline: pre-filled values from the start, others once they've been left
    const [pickerTouched, setPickerTouched] = useState<Record<string, boolean>>(() => ({
        firstName: Boolean((form.getValues('firstName') || '').trim()),
        lastName: Boolean((form.getValues('lastName') || '').trim()),
        phone: Boolean(stripNonDigits(form.getValues('phone') || '')),
    }));
    const pendingSavedAddressRef = useRef<AddressBookItem | null>(null);

    const rawSavedAddresses = useMemo(() => getAddressBookFromCustomer(customerProfile), [customerProfile]);
    const pickerAddresses = useMemo(() => {
        const profileKeys = new Set(rawSavedAddresses.map((a) => getAddressKey(a)));
        return [...rawSavedAddresses, ...justSavedAddresses.filter((a) => !profileKeys.has(getAddressKey(a)))];
    }, [rawSavedAddresses, justSavedAddresses]);

    const createAddressFetcher = useScapiFetcher('shopperCustomers', 'createCustomerAddress', {
        params: { path: { customerId: customerId ?? '' } },
        body: {} as ShopperCustomers.schemas['CustomerAddress'],
    });
    const isSavingToAddressBook = createAddressFetcher.state !== 'idle';

    const openPicker = (address: AddressBookItem) => {
        setPickerSelectedId(address.id);
        setPickerFirstName(address.firstName);
        setPickerLastName(address.lastName);
        setPickerPhone(stripNonDigits(stripCountryCode(address.phone || '')).slice(-10));
        setPickerError(null);
        setSignedInStage('picker');
    };

    useScapiFetcherEffect(createAddressFetcher, {
        onSuccess: () => {
            const saved = pendingSavedAddressRef.current;
            if (!saved) return;
            pendingSavedAddressRef.current = null;
            setJustSavedAddresses((prev) => [...prev, saved]);
            openPicker(saved);
        },
        onError: () => {
            if (!pendingSavedAddressRef.current) return;
            pendingSavedAddressRef.current = null;
            toast.error(tErrors('checkout.addressValidationFailed'));
        },
    });

    const handleSignedInSave = (data: ShippingAddressData) => {
        // Hold the checkout on this step — saving to the address book changes the profile, which
        // would otherwise let the basket-computed step jump ahead before "Use this address".
        // (onEdit pins the checkout's editing step to Shipping Address.)
        onEdit();

        const phone = stripNonDigits(data.phone || '').slice(-10);
        const address: AddressBookItem = {
            id: `addr_${Date.now()}`,
            firstName: data.firstName,
            lastName: data.lastName,
            address1: data.address1,
            address2: data.address2 || undefined,
            city: data.city,
            stateCode: data.stateCode || '',
            postalCode: data.postalCode,
            countryCode: data.countryCode || DEFAULT_COUNTRY_CODE,
            phone: phone || undefined,
            addressType: data.addressType || undefined,
        };

        const existing = pickerAddresses.find((a) => getAddressKey(a) === getAddressKey(address));
        if (existing) {
            openPicker({ ...existing, phone: phone || existing.phone });
            return;
        }

        pendingSavedAddressRef.current = address;
        const { id, addressType, ...rest } = address;
        void createAddressFetcher.submit({
            ...rest,
            addressId: id,
            ...(addressType && { c_addressType: addressType }),
        } as ShopperCustomers.schemas['CustomerAddress']);
    };

    // Same rules as the address form (see createShippingAddressSchema)
    const pickerNameError = (value: string, label: string): string | null =>
        NAME_PATTERN.test(value.trim()) ? null : validMessage(label);
    const pickerPhoneError = INDIAN_MOBILE_PATTERN.test(pickerPhone) ? null : validMessage('mobile number');
    const pickerFieldErrors = {
        firstName: pickerNameError(pickerFirstName, 'first name'),
        lastName: pickerNameError(pickerLastName, 'last name'),
        phone: pickerPhoneError,
    };
    const isPickerValid =
        Boolean(pickerSelectedId) &&
        !pickerFieldErrors.firstName &&
        !pickerFieldErrors.lastName &&
        !pickerFieldErrors.phone;
    const visiblePickerError = (field: keyof typeof pickerFieldErrors) =>
        pickerTouched[field] ? pickerFieldErrors[field] : null;
    const pickerValues: Record<string, string> = {
        firstName: pickerFirstName,
        lastName: pickerLastName,
        phone: pickerPhone,
    };
    // Leaving any field shows the errors of every filled field at once, not one by one
    const touchPickerField = (field: string) => () =>
        setPickerTouched((prev) => {
            const next = { ...prev, [field]: true };
            for (const [key, value] of Object.entries(pickerValues)) if (value.trim()) next[key] = true;
            return next;
        });
    // Browser autofill changes a field without focusing it — treat that as touched so its error shows
    const touchIfAutofilled = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        if (typeof document !== 'undefined' && e.target !== document.activeElement) {
            setPickerTouched((prev) => ({ ...prev, [field]: true }));
        }
    };

    const handleUseThisAddress = () => {
        const address = pickerAddresses.find((a) => a.id === pickerSelectedId);
        if (!address) {
            setPickerError('Please select an address.');
            return;
        }
        if (!isPickerValid) {
            setPickerTouched({ firstName: true, lastName: true, phone: true });
            return;
        }
        setPickerError(null);
        const formData = addressToFormData({
            ...address,
            firstName: pickerFirstName.trim(),
            lastName: pickerLastName.trim(),
            phone: pickerPhone,
        });
        onSubmit(formData);
    };

    // "Add New Address" goes back to a blank address form, keeping the name / phone entered here.
    const handleAddNewAddress = () => {
        form.reset({
            ...form.getValues(),
            firstName: pickerFirstName,
            lastName: pickerLastName,
            phone: pickerPhone,
            countryCode: DEFAULT_COUNTRY_CODE,
            address1: '',
            address2: '',
            city: '',
            stateCode: '',
            postalCode: '',
        });
        setPickerError(null);
        setSignedInStage('form');
    };

    const isFormValid = form.formState.isValid;

    const handleFormSubmit = (data: ShippingAddressData) => {
        if (isSignedIn) {
            handleSignedInSave(data);
            return;
        }
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            if (value != null && value !== '') {
                formData.append(key, value);
            }
        });
        if (!formData.has('countryCode')) {
            formData.append('countryCode', DEFAULT_COUNTRY_CODE);
        }
        onSubmit(formData);
    };

    // A step not yet reached (or completed) is what should render as the disabled,
    // title-only container — not merely whether an address happens to be empty (a
    // basket can already have an address saved before the shopper reaches this step).
    const isUpcomingStep = !isCompleted && !isEditing;

    const stepTitle = (
        <span
            className={cn(
                'text-2xl font-bold tracking-tight',
                isCompleted && !isEditing ? 'text-[#3F3F46]' : 'text-[color:var(--checkout-step-upcoming)]'
            )}>
            {t('shippingAddress.title', { defaultValue: 'Shipping Address' })}
        </span>
    );

    const showPicker = isSignedIn && signedInStage === 'picker';

    return (
        <ToggleCard
            id="shipping-address"
            title={stepTitle as React.ReactNode}
            editing={isEditing}
            className={isEditing ? 'shipping-address-editing' : undefined}
            disabled={isUpcomingStep}
            disableEdit={isUpcomingStep}
            hideContent={isUpcomingStep}
            onEdit={onEdit}
            editLabel={t('common.edit')}
            editButtonClassName="h-auto py-0 px-0 has-[>svg]:px-0 no-underline hover:no-underline border-b-[1.5px] border-current pb-px leading-5 w-fit"
            showHeaderSeparator
            isLoading={isLoading}>
            <ToggleCardEdit>
                {showPicker ? (
                    // Desktop: the card content's 24px padding is the only edge spacing — no extra
                    // top/bottom here, and the fields span the full width (phone = one column).
                    <div className="flex flex-col pt-2 pb-2 min-[787px]:py-0">
                        {pickerError && (
                            <div
                                role="alert"
                                className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-2.5 rounded text-sm max-w-[677px] mb-4">
                                {pickerError}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-[677px] min-[787px]:max-w-none mb-4">
                            <div>
                                <FloatingLabelInput
                                    id="picker-first-name"
                                    label="First Name *"
                                    type="text"
                                    value={pickerFirstName}
                                    onChange={(e) => {
                                        setPickerFirstName(e.target.value);
                                        touchIfAutofilled('firstName', e);
                                    }}
                                    onBlur={touchPickerField('firstName')}
                                    status={visiblePickerError('firstName') ? 'error' : 'default'}
                                />
                                {visiblePickerError('firstName') && (
                                    <p className="text-destructive text-sm mt-1">{visiblePickerError('firstName')}</p>
                                )}
                            </div>
                            <div>
                                <FloatingLabelInput
                                    id="picker-last-name"
                                    label="Last Name *"
                                    type="text"
                                    value={pickerLastName}
                                    onChange={(e) => {
                                        setPickerLastName(e.target.value);
                                        touchIfAutofilled('lastName', e);
                                    }}
                                    onBlur={touchPickerField('lastName')}
                                    status={visiblePickerError('lastName') ? 'error' : 'default'}
                                />
                                {visiblePickerError('lastName') && (
                                    <p className="text-destructive text-sm mt-1">{visiblePickerError('lastName')}</p>
                                )}
                            </div>
                        </div>

                        <div className="w-full sm:max-w-[330.5px] min-[787px]:w-[calc(50%-8px)] min-[787px]:max-w-none mb-4">
                            <FloatingLabelInput
                                id="picker-phone"
                                label="Phone Number *"
                                prefix="+91"
                                type="tel"
                                inputMode="numeric"
                                maxLength={10}
                                value={pickerPhone}
                                onChange={(e) => {
                                    setPickerPhone(stripNonDigits(e.target.value).slice(0, 10));
                                    touchIfAutofilled('phone', e);
                                }}
                                onBlur={touchPickerField('phone')}
                                status={visiblePickerError('phone') ? 'error' : 'default'}
                            />
                            {visiblePickerError('phone') && (
                                <p className="text-destructive text-sm mt-1">{visiblePickerError('phone')}</p>
                            )}
                        </div>

                        <div className="w-full max-w-[677px] min-[787px]:max-w-none mb-3">
                            <SavedAddressDropdown
                                addresses={pickerAddresses}
                                value={pickerSelectedId}
                                onValueChange={setPickerSelectedId}
                                label="Address"
                                placeholder="Choose Address"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={handleAddNewAddress}
                            className="font-lato w-fit mb-6 text-xs font-normal text-black border-b border-black pb-0.5 hover:opacity-80">
                            Add a New Address
                        </button>

                        <Button
                            type="button"
                            disabled={isLoading || !isPickerValid}
                            onClick={handleUseThisAddress}
                            className="checkout-save-address-btn w-full sm:w-[320px] h-[48px] rounded-full bg-[#555555] hover:bg-black text-white text-[15px] font-medium shadow-none transition-colors">
                            {isLoading ? t('shippingAddress.saving') : 'Use this address'}
                        </Button>
                    </div>
                ) : (
                    <Form {...form}>
                        <form
                            onSubmit={(e) => void form.handleSubmit(handleFormSubmit)(e)}
                            className="flex flex-col gap-4 pt-2 pb-2">
                            <AddressFormFields
                                form={form}
                                showPhone={true}
                                showCountry={true}
                                autoFocus={isEditing}
                                autoFocusField="firstName"
                                countryCode={DEFAULT_COUNTRY_CODE}
                                labelsAsPlaceholders={true}
                                showAddressType={true}
                            />
                            <div className="pt-2">
                                <Button
                                    type="submit"
                                    disabled={isLoading || isSavingToAddressBook || !isFormValid}
                                    aria-label={isLoading ? t('shippingAddress.saving') : t('shippingAddress.continue')}
                                    className="checkout-save-address-btn w-full sm:w-[320px] h-[48px] rounded-full bg-[#555555] hover:bg-black text-white text-[15px] font-medium shadow-none transition-colors">
                                    {isLoading || isSavingToAddressBook ? t('shippingAddress.saving') : 'Save Address'}
                                </Button>
                            </div>
                        </form>
                    </Form>
                )}
            </ToggleCardEdit>

            <ToggleCardSummary>
                {shippingAddress && !isAddressEmpty(shippingAddress) ? (
                    <ShippingAddressDisplay address={shippingAddress} displayPhone={true} />
                ) : (
                    <p className="text-sm text-muted-foreground">{t('shippingAddress.completePreviousSteps')}</p>
                )}
            </ToggleCardSummary>
        </ToggleCard>
    );
}
